import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "../../components/ui/sheet";
import { Tag, Plus, Trash, PencilSimple, Image as ImageIcon, Images } from "@phosphor-icons/react";
import { toast } from "sonner";

const FILE_TYPES = [
  { v: "image", label: "Image (jpg/png)" },
  { v: "video", label: "Video (mp4/mov)" },
  { v: "pdf", label: "PDF / Document" },
  { v: "any", label: "Any file" },
];
const emptyRule = { id: "", brand_id: "", product_category: "", active: true, placeholders: [] };

export default function MediaRequirements() {
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [rules, setRules] = useState([]);
  const [newBrand, setNewBrand] = useState("");
  const [brandBusy, setBrandBusy] = useState(false);
  const [rule, setRule] = useState(null);
  const [ruleBusy, setRuleBusy] = useState(false);

  const loadBrands = () => api.get("/service-brands").then((r) => setBrands(r.data)).catch(() => {});
  const loadRules = () => api.get("/media-requirements").then((r) => setRules(r.data)).catch(() => {});
  useEffect(() => {
    loadBrands(); loadRules();
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const addBrand = async () => {
    if (!newBrand.trim()) { toast.error("Enter a brand name"); return; }
    setBrandBusy(true);
    try { await api.post("/service-brands", { name: newBrand.trim() }); setNewBrand(""); toast.success("Brand added"); loadBrands(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBrandBusy(false); }
  };
  const toggleBrand = async (b) => {
    try { await api.put(`/service-brands/${b.id}`, { name: b.name, active: !b.active }); loadBrands(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delBrand = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"? Any media rules for this brand will also be removed.`)) return;
    try { await api.delete(`/service-brands/${b.id}`); toast.success("Brand deleted"); loadBrands(); loadRules(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openRule = (r) => setRule(r ? { ...r, brand_id: r.brand_id || "" } : { ...emptyRule });
  const addPh = () => setRule((s) => ({ ...s, placeholders: [...s.placeholders, { name: "", file_type: "image", required: true }] }));
  const updPh = (i, k, v) => setRule((s) => ({ ...s, placeholders: s.placeholders.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const rmPh = (i) => setRule((s) => ({ ...s, placeholders: s.placeholders.filter((_, idx) => idx !== i) }));

  const saveRule = async () => {
    if (!rule.product_category) { toast.error("Select a product category"); return; }
    const phs = rule.placeholders.filter((p) => (p.name || "").trim());
    if (!phs.length) { toast.error("Add at least one placeholder"); return; }
    setRuleBusy(true);
    const body = { brand_id: rule.brand_id || "", product_category: rule.product_category, active: rule.active, placeholders: phs };
    try {
      if (rule.id) await api.put(`/media-requirements/${rule.id}`, body);
      else await api.post("/media-requirements", body);
      toast.success("Media requirement saved");
      setRule(null); loadRules();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRuleBusy(false); }
  };
  const delRule = async (r) => {
    if (!window.confirm("Delete this media requirement rule?")) return;
    try { await api.delete(`/media-requirements/${r.id}`); toast.success("Rule deleted"); loadRules(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const catNames = cats.map((c) => c.name);

  return (
    <>
      <PageHeader title="Media Requirements" subtitle="Define which photos/videos/documents must be captured per Brand & Product Category" />
      <div className="p-8 max-w-4xl space-y-6">
        <Card className="rounded-sm p-6 border-border" data-testid="brands-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-indigo-100 text-indigo-700 flex items-center justify-center"><Tag size={20} weight="bold" /></div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold">Brands</h3>
              <p className="text-sm text-muted-foreground mt-1">Manage the brand list used on jobs and in media rules (e.g. Helium, Voltas, LG).</p>
              <div className="flex gap-2 mt-4">
                <Input value={newBrand} onChange={(e) => setNewBrand(e.target.value)} placeholder="New brand name" className="rounded-sm" data-testid="brand-name-input" onKeyDown={(e) => e.key === "Enter" && addBrand()} />
                <Button className="rounded-sm gap-1.5 shrink-0" onClick={addBrand} disabled={brandBusy} data-testid="brand-add"><Plus size={15} /> Add</Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="brands-list">
                {brands.length === 0 && <p className="text-xs text-muted-foreground">No brands yet.</p>}
                {brands.map((b) => (
                  <div key={b.id} className={`flex items-center gap-2 border rounded-sm px-2.5 py-1.5 text-sm ${b.active ? "border-border" : "border-dashed opacity-60"}`} data-testid={`brand-${b.id}`}>
                    <span className="font-medium">{b.name}</span>
                    <Switch checked={b.active} onCheckedChange={() => toggleBrand(b)} className="scale-75" />
                    <button onClick={() => delBrand(b)} className="text-destructive" title="Delete"><Trash size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card className="rounded-sm p-6 border-border" data-testid="rules-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-rose-100 text-rose-700 flex items-center justify-center"><Images size={20} weight="bold" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold">Upload Requirements</h3>
                  <p className="text-sm text-muted-foreground mt-1">Placeholders appear on matching jobs. Rules with brand "All brands" apply to every brand in that category.</p>
                </div>
                <Button className="rounded-sm gap-1.5 shrink-0" onClick={() => openRule(null)} data-testid="rule-add"><Plus size={15} /> New Rule</Button>
              </div>
              <div className="mt-4 space-y-2" data-testid="rules-list">
                {rules.length === 0 && <p className="text-xs text-muted-foreground">No rules yet. Add one to require media on jobs.</p>}
                {rules.map((r) => (
                  <div key={r.id} className="border border-border rounded-sm p-3" data-testid={`rule-${r.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm">{r.brand_name || "All brands"} · <span className="text-primary">{r.product_category}</span> {!r.active && <span className="text-xs text-muted-foreground">(inactive)</span>}</p>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{r.placeholders.map((p) => `${p.name}${p.required ? "*" : ""}`).join(", ") || "No placeholders"}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="rounded-sm" onClick={() => openRule(r)} data-testid={`rule-edit-${r.id}`}><PencilSimple size={15} /></Button>
                        <Button variant="ghost" size="icon" className="rounded-sm text-destructive" onClick={() => delRule(r)} data-testid={`rule-delete-${r.id}`}><Trash size={15} /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Sheet open={!!rule} onOpenChange={(o) => !o && setRule(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="rule-form">
          <SheetHeader><SheetTitle className="font-heading">{rule?.id ? "Edit" : "New"} Media Rule</SheetTitle></SheetHeader>
          {rule && (
            <div className="space-y-4 py-6">
              <div>
                <Label className="label-caps">Brand</Label>
                <Select value={rule.brand_id || "__all__"} onValueChange={(v) => setRule({ ...rule, brand_id: v === "__all__" ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1.5" data-testid="rule-brand"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All brands</SelectItem>
                    {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-caps">Product Category</Label>
                {catNames.length ? (
                  <Select value={rule.product_category} onValueChange={(v) => setRule({ ...rule, product_category: v })}>
                    <SelectTrigger className="rounded-sm mt-1.5" data-testid="rule-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{catNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={rule.product_category} onChange={(e) => setRule({ ...rule, product_category: e.target.value })} placeholder="e.g. AC" className="rounded-sm mt-1.5" data-testid="rule-category-input" />
                )}
              </div>
              <div className="flex items-center justify-between border-y border-border py-3">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <Label className="label-caps mb-0">Placeholders</Label>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={addPh} data-testid="rule-add-ph"><Plus size={14} /> Add</Button>
                </div>
              </div>
              <div className="space-y-3" data-testid="rule-ph-list">
                {rule.placeholders.length === 0 && <p className="text-xs text-muted-foreground">No placeholders yet.</p>}
                {rule.placeholders.map((p, i) => (
                  <div key={i} className="border border-border rounded-sm p-3 space-y-2" data-testid={`rule-ph-${i}`}>
                    <div className="flex gap-2">
                      <Input value={p.name} onChange={(e) => updPh(i, "name", e.target.value)} placeholder="Placeholder name (e.g. Serial No Indoor)" className="rounded-sm" data-testid={`ph-name-${i}`} />
                      <Button variant="ghost" size="icon" className="rounded-sm text-destructive shrink-0" onClick={() => rmPh(i)} data-testid={`ph-remove-${i}`}><Trash size={15} /></Button>
                    </div>
                    <div className="flex items-center gap-3">
                      <Select value={p.file_type} onValueChange={(v) => updPh(i, "file_type", v)}>
                        <SelectTrigger className="rounded-sm flex-1" data-testid={`ph-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{FILE_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <div className="flex items-center gap-2 shrink-0">
                        <Switch checked={p.required} onCheckedChange={(v) => updPh(i, "required", v)} data-testid={`ph-required-${i}`} />
                        <span className="text-xs text-muted-foreground">Mandatory</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={rule.active} onCheckedChange={(v) => setRule({ ...rule, active: v })} data-testid="rule-active" />
                <span className="text-sm">Active</span>
              </div>
            </div>
          )}
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setRule(null)}>Cancel</Button>
            <Button className="rounded-sm" onClick={saveRule} disabled={ruleBusy} data-testid="rule-save">{ruleBusy ? "Saving…" : "Save Rule"}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
